import { Button, Grid, GridColumn, Heading, List, ListItem, Text } from 'telia-front-react';
import { observer } from 'mobx-react';
import React from 'react';
import UiNotice from '../components/UiNotice';
import { AccountReturnParams } from '../pages/AccountPage';
import { FetchStatus } from '../util/ApiUtil';
import AddEmailButton from './AddEmailButton';
import ChangePasswordButton from './ChangePasswordButton';
import LoginMethodsAccordionItem from './LoginMethodsAccordionItem';
import useStores from '../stores/UseStores';
import { ConfirmationType } from './RemovalConfirmationModal';

interface EmailItem {
  label: string;
  name: string;
}

interface EmailAccordionItemProps {
  returnParams: AccountReturnParams;
}

const EmailAccordionItem = observer((props: EmailAccordionItemProps): React.JSX.Element => {
  const { returnParams } = props;
  const {
    accountInfoStore,
    translateStore: { translate },
    emailRemoveStore: { removeEmailStatuses },
  } = useStores();

  const renderEmailDeleteButton = (name: string) => {
    if (accountInfoStore.showAddPersonalCodeButton && accountInfoStore.emails.length === 1) {
      return null;
    }

    return (
      <GridColumn width={['min-sm']}>
        <Button
          size="small"
          variant="secondary"
          type="withdrawal"
          text={translate('account.email.remove.button')}
          iconLeft="#close"
          isLoading={removeEmailStatuses.get(name) === FetchStatus.FETCHING}
          onClick={async () => {
            await accountInfoStore.startAccountInfoRemoval(ConfirmationType.EMAIL_REMOVAL, name);
          }}
        />
      </GridColumn>
    );
  };

  const renderListItem = (item: EmailItem): React.JSX.Element => {
    const { name, label } = item;

    return (
      <ListItem key={name}>
        <Grid noWrap={['sm']} align={['middle-xs']}>
          <GridColumn width={['max-sm']}>
            <Heading level={3} className="h5 text-size-normal">
              <>
                <span className="text-small text-light">{label}</span>
                <br />
                <span className="text-bold">{name}</span>
              </>
            </Heading>
          </GridColumn>
          {renderEmailDeleteButton(name)}
        </Grid>
      </ListItem>
    );
  };

  const renderList = () => {
    return (
      <Text>
        <UiNotice
          only={[
            'account.email.remove.success',
            'account.email.add.started',
            'account.email.add.success',
            'account.reset_password.success',
            'account.email.add.duplicate.error',
            'accountMerge.success.email',
            'change_password.success',
          ]}
          separatorSpacing="xxsmall"
        />
        <AddEmailButton returnParams={returnParams} />
        {!!accountInfoStore.emails?.length && (
          <List type="icon" bordered={true} fullWidth={true} iconPosition="middle">
            {accountInfoStore.emails?.map((email) => renderListItem({ label: translate('account.email.name'), name: email.address }))}
            <ListItem key="passwordChange">
              <Grid noWrap={['sm']} align={['middle-xs']}>
                <GridColumn width={['max-sm']}>
                  <Heading level={3} className="h5 text-size-normal">
                    <>
                      <span className="text-small text-light">{translate('account.password.name')}</span>
                      <br />
                      <span className="text-bold">********</span>
                    </>
                  </Heading>
                </GridColumn>
                <GridColumn width={['min-sm']}>
                  <ChangePasswordButton hasEmail={!!accountInfoStore.emails?.length} />
                </GridColumn>
              </Grid>
            </ListItem>
          </List>
        )}
      </Text>
    );
  };

  return (
    <>
      <LoginMethodsAccordionItem
        id={'email-password'}
        title={translate('account.email.accordion.label')}
        icon={'#email-write'}
        subTitle={translate('account.email.accordion.description')}
      >
        {renderList()}
      </LoginMethodsAccordionItem>
    </>
  );
});

export default EmailAccordionItem;
